const decodeHtmlEntities = value => {
    if (typeof document === 'undefined') return value

    const textarea = document.createElement('textarea')
    textarea.innerHTML = value
    return textarea.value
}

export const normalizeSelectionText = value => {
    if (!value) return ''

    return decodeHtmlEntities(String(value))
        .replace(/\u00a0/g, ' ')
        .replace(/&nbsp;/gi, ' ')
        .replace(/\s+/g, ' ')
        .trim()
}

const displaySelectionText = value => {
    if (!value) return ''

    return String(value).replace(/&nbsp;/gi, ' ')
}

export const isDuplicatePreviewDescription = (selection, description) => {
    const normalizedSelection = normalizeSelectionText(selection)
    const normalizedDescription = normalizeSelectionText(description)
    return !!normalizedSelection && normalizedSelection === normalizedDescription
}

const trimUrlToken = value =>
    normalizeSelectionText(value)
        .replace(/[)\].,!?;:'"]+$/g, '')
        .replace(/\/+$/g, '')

const urlTokenBoundary = value => !value || /[\s)\].,!?;:'"]/.test(value)

const bodyTextIncludesUrlVariant = (bodyText, variant) => {
    let searchFrom = 0

    while (searchFrom < bodyText.length) {
        const index = bodyText.indexOf(variant, searchFrom)
        if (index < 0) return false

        const nextChar = bodyText[index + variant.length]
        if (urlTokenBoundary(nextChar)) return true

        searchFrom = index + variant.length
    }

    return false
}

const urlTextVariants = address => {
    const normalizedAddress = trimUrlToken(address)
    if (!normalizedAddress) return []

    const variants = new Set([normalizedAddress])
    const addressWithScheme = /^[a-z][a-z0-9+.-]*:\/\//i.test(normalizedAddress)
        ? normalizedAddress
        : `https://${normalizedAddress}`

    try {
        const parsed = new URL(addressWithScheme)
        const hostPath = trimUrlToken(
            `${parsed.host}${parsed.pathname}${parsed.search}${parsed.hash}`,
        )
        const href = trimUrlToken(parsed.href)

        if (hostPath) variants.add(hostPath)
        if (href) variants.add(href)
        if (hostPath.startsWith('www.')) variants.add(hostPath.replace(/^www\./, ''))
    } catch (e) {
        // Keep the normalized string fallback for malformed or partial URLs.
    }

    return [...variants].filter(Boolean)
}

const deltaUrlStrings = delta => {
    if (!delta) return []

    let parsedDelta = delta
    if (typeof parsedDelta === 'string') {
        try {
            parsedDelta = JSON.parse(parsedDelta)
        } catch (e) {
            return []
        }
    }

    if (!parsedDelta?.ops || !Array.isArray(parsedDelta.ops)) return []

    return parsedDelta.ops.flatMap(op => {
        const urls = []
        const linkedUrl = op?.attributes?.link
        if (typeof linkedUrl === 'string') urls.push(linkedUrl)

        if (typeof op?.insert === 'string') {
            const matches = op.insert.match(/https?:\/\/[^\s]+/gi)
            if (matches) urls.push(...matches)
        }

        return urls
    })
}

export const answerBodyIncludesUrl = (answer, address) => {
    const variants = urlTextVariants(address).map(variant => variant.toLowerCase())
    if (!variants.length) return false

    const bodyText = normalizeSelectionText(
        [answer?.content, answer?.display_content].filter(Boolean).join(' '),
    ).toLowerCase()
    if (bodyText && variants.some(variant => bodyTextIncludesUrlVariant(bodyText, variant))) {
        return true
    }

    const deltaUrls = [
        ...deltaUrlStrings(answer?.delta_json),
        ...deltaUrlStrings(answer?.content_json),
    ].flatMap(url => urlTextVariants(url).map(variant => variant.toLowerCase()))

    return deltaUrls.some(deltaUrl => variants.includes(deltaUrl))
}

export const getDisplayUrlSelection = ({ answer, address, previewDescription, isYoutube }) => {
    const selectionText = displaySelectionText(answer?.url?.selection)
    if (!selectionText) return ''

    if (answerBodyIncludesUrl(answer, address)) return ''

    if (!isYoutube && isDuplicatePreviewDescription(selectionText, previewDescription)) return ''

    return selectionText
}
